import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

type AdminTab = 'teachers' | 'exams' | 'disputes' | 'revenue';

interface PendingTeacher {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  subjects?: string[];
  location?: string;
  experience?: string;
  qualification?: string;
  qualifications?: Array<{
    level: string;
    course: string;
    specialization?: string;
    institute?: string;
    yearOfCompletion?: string;
  }>;
  status: string;
  certificationStatus?: string;
  certificationScore?: number;
  profileCompletionPercentage?: number;
  profileMissingFields?: string[];
  readyForApproval?: boolean;
  createdAt?: string;
}

interface PendingExam {
  examId: string;
  title: string;
  subject: string;
  status: string;
  adminApprovalStatus?: string;
  description?: string;
  standardLevel?: string;
  totalMarks?: number;
  duration?: number;
  totalQuestions?: number;
  passingPercentage?: number;
  createdAt?: string;
  publicDashboardEnabled?: boolean;
  publicDashboardCategory?: 'GRAND_TEST' | 'TALENT_TEST';
  publicDashboardOrganizer?: string;
  schoolAccessEnabled?: boolean;
  schoolAccessCode?: string;
  guestReferralEnabled?: boolean;
  guestAttemptFee?: number;
}

interface PendingExamSummary {
  _id?: string;
  examId: string;
  title: string;
  subject: string;
  status: string;
  adminApprovalStatus?: string;
  createdAt?: string;
}

interface ExamQuestion {
  questionId: string;
  questionText: string;
  questionType: string;
  marks: number;
  difficulty?: string;
  sequenceNumber?: number;
}

interface DisputeItem {
  id: string;
  reason: string;
  description: string;
  status: string;
  createdAt?: string;
  escrow?: {
    id?: string;
    amount?: number;
    currency?: string;
    serviceType?: string;
  };
}

interface RevenueSummary {
  currency: string;
  topupAmount: number;
  subscriptionRevenue: number;
  classPaymentRevenue: number;
  platformFeeRevenue: number;
  releasedEscrowAmount: number;
  totalWalletTransactions: number;
  totalEscrowTransactions: number;
  totalEscrows: number;
}

const tabConfig: Array<{
  id: AdminTab;
  label: string;
  caption: string;
}> = [
  { id: 'teachers', label: 'Teacher verification', caption: 'Identity, profile, and readiness checks' },
  { id: 'exams', label: 'Exam approvals', caption: 'Content review and publishing decisions' },
  { id: 'disputes', label: 'Dispute desk', caption: 'Resolution flow for escalated payment issues' },
  { id: 'revenue', label: 'Revenue watch', caption: 'Tracked financial movement across the platform' },
];

const formatCurrency = (value?: number, currency = 'INR') =>
  `${currency} ${Number(value || 0).toFixed(2)}`;

const formatDate = (value?: string) => {
  if (!value) return 'Not available';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const AdminOperationsDashboard: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('teachers');
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState<PendingTeacher[]>([]);
  const [exams, setExams] = useState<PendingExamSummary[]>([]);
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [revenue, setRevenue] = useState<RevenueSummary | null>(null);
  const [selectedExamId, setSelectedExamId] = useState<string | null>(null);
  const [selectedExam, setSelectedExam] = useState<PendingExam | null>(null);
  const [manageableExams, setManageableExams] = useState<PendingExam[]>([]);
  const [examQuestions, setExamQuestions] = useState<ExamQuestion[]>([]);
  const [examLoading, setExamLoading] = useState(false);
  const [examNote, setExamNote] = useState<Record<string, string>>({});
  const [examPublicCategory, setExamPublicCategory] = useState<Record<string, 'GRAND_TEST' | 'TALENT_TEST'>>({});
  const [examOrganizer, setExamOrganizer] = useState<Record<string, string>>({});
  const [examSchoolAccessEnabled, setExamSchoolAccessEnabled] = useState<Record<string, boolean>>({});
  const [examSchoolAccessCode, setExamSchoolAccessCode] = useState<Record<string, string>>({});
  const [examGuestReferralEnabled, setExamGuestReferralEnabled] = useState<Record<string, boolean>>({});
  const [examGuestAttemptFee, setExamGuestAttemptFee] = useState<Record<string, string>>({});
  const [disputeResolution, setDisputeResolution] = useState<Record<string, string>>({});
  const [disputeNotes, setDisputeNotes] = useState<Record<string, string>>({});
  const [busyTeacherId, setBusyTeacherId] = useState<string | null>(null);
  const [busyExamId, setBusyExamId] = useState<string | null>(null);
  const [busyDisputeId, setBusyDisputeId] = useState<string | null>(null);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [teacherRes, examRes, manageableExamRes, disputeRes, revenueRes] = await Promise.allSettled([
        api.get('/admin/teachers/pending'),
        api.get('/admin/exams/pending'),
        api.get('/admin/exams/manageable'),
        api.get('/admin/disputes?status=OPEN'),
        api.get('/admin/revenue/summary'),
      ]);

      setTeachers(teacherRes.status === 'fulfilled' && Array.isArray(teacherRes.value.data) ? teacherRes.value.data : []);
      setExams(examRes.status === 'fulfilled' && Array.isArray(examRes.value.data) ? examRes.value.data : []);
      setManageableExams(
        manageableExamRes.status === 'fulfilled' && Array.isArray(manageableExamRes.value.data)
          ? manageableExamRes.value.data
          : [],
      );
      setDisputes(disputeRes.status === 'fulfilled' && Array.isArray(disputeRes.value.data) ? disputeRes.value.data : []);
      setRevenue(revenueRes.status === 'fulfilled' ? revenueRes.value.data || null : null);
    } catch (error) {
      console.error('Failed to load admin operations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const verifyTeacher = async (teacherId: string, decision: 'APPROVED' | 'REJECTED') => {
    try {
      setBusyTeacherId(teacherId);
      await api.patch(`/admin/teachers/${teacherId}/verify`, { decision });
      await loadAll();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to verify teacher');
    } finally {
      setBusyTeacherId(null);
    }
  };

  const reviewExam = async (examId: string, decision: 'APPROVED' | 'REJECTED') => {
    try {
      setBusyExamId(examId);
      await api.patch(`/admin/exams/${examId}/review`, {
        decision,
        note: examNote[examId] || '',
      });
      await loadAll();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to review exam');
    } finally {
      setBusyExamId(null);
    }
  };

  const updatePublicDashboard = async (examId: string, enabled: boolean) => {
    try {
      setBusyExamId(examId);
      await api.patch(`/admin/exams/${examId}/public-dashboard`, {
        enabled,
        category: examPublicCategory[examId] || 'GRAND_TEST',
        organizer: examOrganizer[examId] || '',
        schoolAccessEnabled: examSchoolAccessEnabled[examId] ?? false,
        schoolAccessCode: examSchoolAccessCode[examId] || '',
        guestReferralEnabled: examGuestReferralEnabled[examId] ?? false,
        guestAttemptFee: Number(examGuestAttemptFee[examId] || 0),
      });
      await loadAll();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to update exam dashboard visibility');
    } finally {
      setBusyExamId(null);
    }
  };

  const viewExam = async (examId: string) => {
    try {
      setExamLoading(true);
      const [examRes, questionRes] = await Promise.all([
        api.get(`/exams/${examId}`),
        api.get(`/exams/${examId}/questions`),
      ]);
      setSelectedExamId(examId);
      setSelectedExam(examRes.data || null);
      setExamQuestions(Array.isArray(questionRes.data) ? questionRes.data : []);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to load exam details');
    } finally {
      setExamLoading(false);
    }
  };

  const resolveDispute = async (disputeId: string) => {
    const resolution = (disputeResolution[disputeId] || '').trim();
    if (!resolution) {
      alert('Enter resolution text.');
      return;
    }

    try {
      setBusyDisputeId(disputeId);
      await api.post(`/admin/disputes/${disputeId}/resolve`, {
        resolution,
        notes: disputeNotes[disputeId] || '',
      });
      await loadAll();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to resolve dispute');
    } finally {
      setBusyDisputeId(null);
    }
  };

  const totalRevenue = useMemo(() => {
    if (!revenue) return 0;
    return (
      Number(revenue.subscriptionRevenue || 0) +
      Number(revenue.classPaymentRevenue || 0) +
      Number(revenue.platformFeeRevenue || 0)
    );
  }, [revenue]);

  const metrics = useMemo(
    () => [
      {
        label: 'Teachers awaiting review',
        value: teachers.length,
        detail: 'Profiles that need verification',
        icon: CheckBadgeIcon,
      },
      {
        label: 'Exams in approval queue',
        value: exams.length,
        detail: 'Submitted content waiting for admin review',
        icon: ClipboardDocumentCheckIcon,
      },
      {
        label: 'Open disputes',
        value: disputes.length,
        detail: 'Escalated issues requiring resolution',
        icon: ExclamationTriangleIcon,
      },
      {
        label: 'Tracked revenue',
        value: formatCurrency(totalRevenue, revenue?.currency || 'INR'),
        detail: 'Subscription, class, and fee revenue combined',
        icon: BanknotesIcon,
      },
    ],
    [teachers.length, exams.length, disputes.length, totalRevenue, revenue?.currency],
  );

  return (
    <div className="space-y-6 text-slate-900">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.18),_transparent_28%),linear-gradient(135deg,_#172554_0%,_#0f172a_48%,_#14532d_100%)] p-6 text-white shadow-[0_30px_80px_-48px_rgba(15,23,42,0.85)] sm:p-8">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.9fr)]">
          <div>
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-100">
              Admin operations
            </span>
            <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
              Run approvals, disputes, and revenue oversight from one control surface.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
              The module is structured around the four admin queues that create operational load. Clear them fast without losing detail.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/admin/reschedule-approvals"
                className="inline-flex items-center rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                Open reschedule approvals
              </Link>
              <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm text-slate-200">
                {loading ? 'Refreshing admin data...' : 'Live admin queues loaded'}
              </div>
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
            <div className="grid gap-3">
              <div className="rounded-[1.25rem] border border-white/10 bg-slate-950/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Operational pressure</p>
                <p className="mt-3 text-3xl font-semibold">{teachers.length + exams.length + disputes.length}</p>
                <p className="mt-2 text-sm text-slate-300">Combined active admin items across people, content, and finance workflows.</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                  <p className="text-xs text-slate-300">Teachers</p>
                  <p className="mt-2 text-2xl font-semibold">{teachers.length}</p>
                </div>
                <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                  <p className="text-xs text-slate-300">Exams</p>
                  <p className="mt-2 text-2xl font-semibold">{exams.length}</p>
                </div>
                <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                  <p className="text-xs text-slate-300">Disputes</p>
                  <p className="mt-2 text-2xl font-semibold">{disputes.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.label}
              className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{metric.label}</p>
                  <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{metric.value}</p>
                  <p className="mt-2 text-sm text-slate-500">{metric.detail}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <Icon className="h-6 w-6 text-slate-700" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="rounded-[2rem] border border-slate-200 bg-white p-3 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)] sm:p-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {tabConfig.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`rounded-[1.5rem] border px-4 py-4 text-left transition ${
                tab === item.id
                  ? 'border-slate-950 bg-slate-950 text-white'
                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <p className="text-sm font-semibold">{item.label}</p>
              <p className={`mt-2 text-xs leading-5 ${tab === item.id ? 'text-slate-300' : 'text-slate-500'}`}>
                {item.caption}
              </p>
            </button>
          ))}
        </div>
      </section>

      {loading && (
        <div className="rounded-[1.75rem] border border-slate-200 bg-white px-6 py-5 text-sm text-slate-500 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
          Loading admin data...
        </div>
      )}

      {!loading && tab === 'teachers' && (
        <section className="space-y-5">
          {teachers.length === 0 ? (
            <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-14 text-center shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <ShieldCheckIcon className="mx-auto h-12 w-12 text-slate-300" />
              <p className="mt-4 text-base font-semibold text-slate-900">No teachers awaiting verification</p>
              <p className="mt-2 text-sm text-slate-500">The review queue is currently clear.</p>
            </div>
          ) : (
            teachers.map((teacher) => (
              <article
                key={teacher.id}
                className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]"
              >
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {teacher.status}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          teacher.readyForApproval ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {teacher.readyForApproval ? 'Ready for approval' : 'Needs attention'}
                      </span>
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">
                      {`${teacher.firstName} ${teacher.lastName}`.trim() || 'Teacher'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-500">
                      {teacher.email || 'No email'}{teacher.phone ? ` • ${teacher.phone}` : ''}{teacher.location ? ` • ${teacher.location}` : ''}
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs text-slate-400">Profile</p>
                      <p className="mt-2 text-lg font-semibold text-slate-950">
                        {Number(teacher.profileCompletionPercentage || 0)}%
                      </p>
                    </div>
                    <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs text-slate-400">Certification</p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">
                        {teacher.certificationStatus || 'NOT_ATTEMPTED'}
                      </p>
                    </div>
                    <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs text-slate-400">Applied</p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">{formatDate(teacher.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)]">
                  <div className="space-y-4">
                    <div className="rounded-[1.5rem] border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Subjects</p>
                      <p className="mt-3 text-sm text-slate-700">{(teacher.subjects || []).join(', ') || 'No subjects listed'}</p>
                    </div>
                    <div className="rounded-[1.5rem] border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Profile readiness</p>
                      {Array.isArray(teacher.profileMissingFields) && teacher.profileMissingFields.length > 0 ? (
                        <p className="mt-3 text-sm text-amber-700">
                          Missing: {teacher.profileMissingFields.join(', ')}
                        </p>
                      ) : (
                        <p className="mt-3 text-sm text-emerald-700">Profile is complete.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Approval action</p>
                    <p className="mt-3 text-sm text-slate-600">
                      {typeof teacher.certificationScore === 'number'
                        ? `Certification score: ${Number(teacher.certificationScore).toFixed(2)}%`
                        : 'Certification score not available.'}
                    </p>
                    <div className="mt-5 flex flex-col gap-3">
                      <button
                        onClick={() => verifyTeacher(teacher.id, 'APPROVED')}
                        disabled={!teacher.readyForApproval || busyTeacherId === teacher.id}
                        className="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busyTeacherId === teacher.id ? 'Saving...' : 'Approve teacher'}
                      </button>
                      <button
                        onClick={() => verifyTeacher(teacher.id, 'REJECTED')}
                        disabled={busyTeacherId === teacher.id}
                        className="rounded-full border border-rose-200 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Reject teacher
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))
          )}
        </section>
      )}

      {!loading && tab === 'exams' && (
        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.9fr)]">
          <div className="space-y-5">
            {exams.length === 0 ? (
              <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-14 text-center shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
                <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-4 text-base font-semibold text-slate-900">No pending exams</p>
                <p className="mt-2 text-sm text-slate-500">There are no exam submissions waiting for review.</p>
              </div>
            ) : (
              exams.map((exam) => (
                <article
                  key={exam.examId || exam._id}
                  className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                          {exam.subject}
                        </span>
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                          {exam.adminApprovalStatus || 'PENDING'}
                        </span>
                      </div>
                      <h2 className="mt-4 text-xl font-semibold tracking-tight text-slate-950">{exam.title}</h2>
                      <p className="mt-2 text-sm text-slate-500">
                        Status: {exam.status} • Created {formatDate(exam.createdAt)}
                      </p>
                    </div>
                    <button
                      onClick={() => viewExam(exam.examId)}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      View exam details
                    </button>
                  </div>

                  <div className="mt-5 rounded-[1.5rem] border border-slate-200 p-4">
                    <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400" htmlFor={`exam-note-${exam.examId}`}>
                      Review note
                    </label>
                    <textarea
                      id={`exam-note-${exam.examId}`}
                      value={examNote[exam.examId] || ''}
                      onChange={(e) => setExamNote((prev) => ({ ...prev, [exam.examId]: e.target.value }))}
                      placeholder="Explain the approval or rejection decision"
                      rows={3}
                      className="mt-3 w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    />
                  </div>

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
                    <button
                      onClick={() => reviewExam(exam.examId, 'REJECTED')}
                      disabled={busyExamId === exam.examId}
                      className="rounded-full border border-rose-200 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Reject exam
                    </button>
                    <button
                      onClick={() => reviewExam(exam.examId, 'APPROVED')}
                      disabled={busyExamId === exam.examId}
                      className="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {busyExamId === exam.examId ? 'Saving...' : 'Approve exam'}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>

          <aside className="rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">Exam detail panel</h2>
              <p className="mt-1 text-sm text-slate-500">Load a submission to inspect structure and question quality.</p>
            </div>
            <div className="space-y-5 px-6 py-5">
              {examLoading ? (
                <p className="text-sm text-slate-500">Loading exam...</p>
              ) : !selectedExamId || !selectedExam ? (
                <p className="text-sm text-slate-500">Select an exam from the queue to inspect it here.</p>
              ) : (
                <>
                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-lg font-semibold text-slate-950">{selectedExam.title}</p>
                    <p className="mt-2 text-sm text-slate-600">{selectedExam.subject}</p>
                    <p className="mt-2 text-xs text-slate-500">
                      {selectedExam.totalQuestions || examQuestions.length} questions • {selectedExam.totalMarks || 0} marks • {selectedExam.duration || 0} min
                    </p>
                    {selectedExam.description ? (
                      <p className="mt-3 text-sm leading-6 text-slate-600">{selectedExam.description}</p>
                    ) : null}
                  </div>

                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Questions ({examQuestions.length})
                    </p>
                    {examQuestions.length === 0 ? (
                      <p className="mt-3 text-sm text-slate-500">No questions found.</p>
                    ) : (
                      <div className="mt-4 space-y-3">
                        {examQuestions.map((question) => (
                          <div key={question.questionId} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-sm font-semibold text-slate-950">
                              Q{question.sequenceNumber || '-'}: {question.questionText}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {question.questionType} • {question.marks} marks • {question.difficulty || 'Difficulty not set'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </aside>

          <div className="xl:col-span-2 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-950">Public dashboard controls</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Enable approved student exams for the unauthenticated public exam dashboard.
                </p>
              </div>
              <Link
                to="/exam-dashboard"
                className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Open public exam dashboard
              </Link>
            </div>

            <div className="mt-6 space-y-4">
              {manageableExams.length === 0 ? (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                  No approved student exams are available for public dashboard configuration.
                </div>
              ) : (
                manageableExams.map((exam) => (
                  <article key={`public-${exam.examId}`} className="rounded-[1.5rem] border border-slate-200 p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {exam.subject}
                          </span>
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              exam.publicDashboardEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {exam.publicDashboardEnabled ? 'Visible on public dashboard' : 'Hidden from public dashboard'}
                          </span>
                        </div>
                        <h3 className="mt-4 text-lg font-semibold text-slate-950">{exam.title}</h3>
                        <p className="mt-2 text-sm text-slate-500">
                          {exam.status} • {exam.totalQuestions || 0} questions • Created {formatDate(exam.createdAt)}
                        </p>
                      </div>
                      <button
                        onClick={() => void updatePublicDashboard(exam.examId, !exam.publicDashboardEnabled)}
                        disabled={busyExamId === exam.examId}
                        className="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busyExamId === exam.examId
                          ? 'Saving...'
                          : exam.publicDashboardEnabled
                            ? 'Disable public access'
                            : 'Enable public access'}
                      </button>
                    </div>

                    <div className="mt-5 grid gap-4 lg:grid-cols-2">
                      <label className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Exam type</span>
                        <select
                          value={examPublicCategory[exam.examId] || exam.publicDashboardCategory || 'GRAND_TEST'}
                          onChange={(e) =>
                            setExamPublicCategory((prev) => ({
                              ...prev,
                              [exam.examId]: e.target.value as 'GRAND_TEST' | 'TALENT_TEST',
                            }))
                          }
                          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                        >
                          <option value="GRAND_TEST">Grand Test</option>
                          <option value="TALENT_TEST">Talent Test</option>
                        </select>
                      </label>
                      <label className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Organizer</span>
                        <input
                          value={examOrganizer[exam.examId] ?? exam.publicDashboardOrganizer ?? ''}
                          onChange={(e) =>
                            setExamOrganizer((prev) => ({
                              ...prev,
                              [exam.examId]: e.target.value,
                            }))
                          }
                          placeholder="School, institute, or campaign name"
                          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                        />
                      </label>
                      <label className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School code</span>
                        <input
                          value={examSchoolAccessCode[exam.examId] ?? exam.schoolAccessCode ?? ''}
                          onChange={(e) =>
                            setExamSchoolAccessCode((prev) => ({
                              ...prev,
                              [exam.examId]: e.target.value.toUpperCase(),
                            }))
                          }
                          placeholder="COMMONCODE"
                          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                        />
                      </label>
                      <label className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School access</span>
                        <select
                          value={String(examSchoolAccessEnabled[exam.examId] ?? exam.schoolAccessEnabled ?? false)}
                          onChange={(e) =>
                            setExamSchoolAccessEnabled((prev) => ({
                              ...prev,
                              [exam.examId]: e.target.value === 'true',
                            }))
                          }
                          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                        >
                          <option value="false">Disabled</option>
                          <option value="true">Enabled</option>
                        </select>
                      </label>
                      <label className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Referral fee access</span>
                        <select
                          value={String(examGuestReferralEnabled[exam.examId] ?? exam.guestReferralEnabled ?? false)}
                          onChange={(e) =>
                            setExamGuestReferralEnabled((prev) => ({
                              ...prev,
                              [exam.examId]: e.target.value === 'true',
                            }))
                          }
                          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                        >
                          <option value="false">Disabled</option>
                          <option value="true">Enabled</option>
                        </select>
                        <input
                          value={examGuestAttemptFee[exam.examId] ?? String(exam.guestAttemptFee ?? '')}
                          onChange={(e) =>
                            setExamGuestAttemptFee((prev) => ({
                              ...prev,
                              [exam.examId]: e.target.value,
                            }))
                          }
                          placeholder="Amount in INR"
                          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                        />
                      </label>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {!loading && tab === 'disputes' && (
        <section className="space-y-5">
          {disputes.length === 0 ? (
            <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-14 text-center shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-slate-300" />
              <p className="mt-4 text-base font-semibold text-slate-900">No open disputes</p>
              <p className="mt-2 text-sm text-slate-500">There are no escalated disputes requiring action.</p>
            </div>
          ) : (
            disputes.map((dispute) => (
              <article
                key={dispute.id}
                className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                      {dispute.status}
                    </span>
                    <h2 className="mt-4 text-xl font-semibold tracking-tight text-slate-950">{dispute.reason}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{dispute.description}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    {dispute.escrow?.serviceType || 'Service not set'} • {formatCurrency(dispute.escrow?.amount, dispute.escrow?.currency || 'INR')}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 xl:grid-cols-2">
                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400" htmlFor={`resolution-${dispute.id}`}>
                      Resolution summary
                    </label>
                    <textarea
                      id={`resolution-${dispute.id}`}
                      value={disputeResolution[dispute.id] || ''}
                      onChange={(e) => setDisputeResolution((prev) => ({ ...prev, [dispute.id]: e.target.value }))}
                      placeholder="State the final resolution"
                      rows={3}
                      className="mt-3 w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    />
                  </div>
                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400" htmlFor={`notes-${dispute.id}`}>
                      Internal note
                    </label>
                    <textarea
                      id={`notes-${dispute.id}`}
                      value={disputeNotes[dispute.id] || ''}
                      onChange={(e) => setDisputeNotes((prev) => ({ ...prev, [dispute.id]: e.target.value }))}
                      placeholder="Optional notes for future audit"
                      rows={3}
                      className="mt-3 w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    onClick={() => resolveDispute(dispute.id)}
                    disabled={busyDisputeId === dispute.id}
                    className="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busyDisputeId === dispute.id ? 'Resolving...' : 'Resolve dispute'}
                  </button>
                </div>
              </article>
            ))
          )}
        </section>
      )}

      {!loading && tab === 'revenue' && (
        <section className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Subscription revenue</p>
              <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                {formatCurrency(revenue?.subscriptionRevenue, revenue?.currency || 'INR')}
              </p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Class payments</p>
              <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                {formatCurrency(revenue?.classPaymentRevenue, revenue?.currency || 'INR')}
              </p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Platform fee revenue</p>
              <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                {formatCurrency(revenue?.platformFeeRevenue, revenue?.currency || 'INR')}
              </p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Total tracked revenue</p>
              <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                {formatCurrency(totalRevenue, revenue?.currency || 'INR')}
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Wallet top-ups</p>
              <p className="mt-3 text-2xl font-semibold text-slate-950">
                {formatCurrency(revenue?.topupAmount, revenue?.currency || 'INR')}
              </p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Released escrow volume</p>
              <p className="mt-3 text-2xl font-semibold text-slate-950">
                {formatCurrency(revenue?.releasedEscrowAmount, revenue?.currency || 'INR')}
              </p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Escrows tracked</p>
              <p className="mt-3 text-2xl font-semibold text-slate-950">{revenue?.totalEscrows || 0}</p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Wallet transactions</p>
              <p className="mt-3 text-2xl font-semibold text-slate-950">{revenue?.totalWalletTransactions || 0}</p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Escrow transactions</p>
              <p className="mt-3 text-2xl font-semibold text-slate-950">{revenue?.totalEscrowTransactions || 0}</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminOperationsDashboard;
